import { Alert, Box, CircularProgress, Drawer } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import TaskTrackingService, { taskErrorCode, taskErrorMessage } from '../../services/TaskTrackingService';
import { openInEditor, showInFiles } from './openContent';
import DubbingTransformDialog from '../dialogs/DubbingTransformDialog';
import EditPostDialog from './EditPostDialog';
import PostTextSection, { PostLanguageStrip } from './PostTextSection';
import PostingSection from './PostingSection';
import SetPostingDateDialog from './SetPostingDateDialog';
import TaskActionDialog from './TaskActionDialog';
import TaskComments from './TaskComments';
import TaskDetails from './TaskDetails';
import TaskDrawerHeader from './TaskDrawerHeader';
import TaskFileDialogs from './TaskFileDialogs';
import { pendingCompletions } from './taskCompletions';
import { useTaskCategories, useTaskPlatforms } from './taskFields';
import TaskSnackbar from './TaskSnackbar';
import TaskHistory from './TaskHistory';
import TaskStages from './TaskStages';
import TaskStepCard, { blockDialog, StepBox, TaskCompleteDialogs, TaskStepActions } from './TaskStepCard';
import { fileWord, shortName } from './taskUi';
import TaskWork from './TaskWork';
import TaskWaitingCard from './TaskWaitingCard';
import TaskWarnings from './TaskWarnings';
import useCompleteStep from './useCompleteStep';
import usePmActions from './usePmActions';
import useTaskConversion from './useTaskConversion';
import useTaskSummary, { refreshTaskSummary } from './useTaskSummary';

// One queue for the whole app so a completion survives the drawer closing. Other files use it from here.
export { pendingCompletions };

/**
 * Right-side task drawer: stage stepper, the actions this user can take, comments and the
 * audit trail. Opens from any task view and from the Files browser chips.
 */
const TaskDrawer = ({ taskId, open, onClose, onChanged, onOpenTask, focusAction, setCanNavigateToEdit }) => {
    useTrackUsage('TaskDrawer', 'dialog', 'tasks', open);
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refused, setRefused] = useState(false);
    const avOnly = !!useTaskSummary()?.avOnly;
    const [users, setUsers] = useState([]);
    const [dialog, setDialog] = useState(null);
    const [snack, setSnack] = useState(null);
    const [linking, setLinking] = useState(false);
    const [creatingFile, setCreatingFile] = useState(false);
    const [startingVideo, setStartingVideo] = useState(false);
    const [busy, setBusy] = useState(false);
    // Posts: the boxes (to wait for their saves), Edit post and the one date
    const postSection = useRef(null);
    const [editingPost, setEditingPost] = useState(false);
    const [settingDate, setSettingDate] = useState(false);
    const [convertOpen, setConvertOpen] = useState(false);
    const categories = useTaskCategories();
    const platforms = useTaskPlatforms();
    const me = useMemo(() => {
        try {
            return (JSON.parse(localStorage.getItem('authUser')) || {}).email?.toLowerCase() || null;
        } catch (e) {
            return null;
        }
    }, []);

    const load = useCallback(async () => {
        if (!taskId) return;
        setLoading(true);
        setError(null);
        setRefused(false);
        try {
            const d = await TaskTrackingService.get(taskId);
            setDetail(d);
            if (!d.task.redacted) {
                setEvents(await TaskTrackingService.events(taskId));
            } else {
                setEvents([]);
            }
        } catch (e) {
            setError(taskErrorMessage(e));
            setRefused(e?.response?.status === 403);
        } finally {
            setLoading(false);
        }
    }, [taskId]);

    useEffect(() => {
        if (open) {
            setDetail(null);
            load();
        }
    }, [open, load]);

    const task = detail?.task;
    const perms = detail?.permissions || {};
    const isAudio = task?.contentType === 'AUDIO';
    const isPost = task?.contentType === 'POST';
    const { conversion, converting, convertLine, loadConversion, atAudioConversion, needsDubbingAudio } =
        useTaskConversion({ open, detail, load });
    const stages = useMemo(() => detail?.stages || [], [detail]);
    const current = stages.find((s) => s.stageKey === task?.currentStageKey);
    // Translation L1/L2 linked to the editor: Start/Complete/Send back also move the editor
    const link = detail?.editorLink || null;
    const linkedLevel = current && link?.linkedStages ? link.linkedStages[current.stageKey] : null;
    const editorBlocked = (reached) => (linkedLevel && link.notReadyReason && !reached ? link.notReadyMessage : null);
    const startBlocked = editorBlocked(link?.editorAtLevel);
    const completeBlocked = editorBlocked(link?.editorPastLevel);
    // One step: there is no Start button. Opening the editor starts a stage done in the editor,
    // but only for its assignee; a PM opening it to look doesn't start someone else's stage.
    const isAssignee = !!me && current?.assignedTo?.toLowerCase() === me;
    const isMe = useCallback((who) => !!me && who?.toLowerCase() === me, [me]);
    const startsOnOpen = !!current && perms.canStart && isAssignee && (current.auto || !!linkedLevel) && !startBlocked;
    const openEditor = () =>
        openInEditor({
            contentType: task.contentType,
            contentId: task.contentId,
            folderPath: detail.contentFolderPath,
            navigate,
            setCanNavigateToEdit,
            language: detail.editorLanguage,
        });
    // People list for pickers, loaded when first needed
    const ensureUsers = useCallback(async () => {
        if (users.length > 0 || !task) return users;
        try {
            const list = await TaskTrackingService.assignableUsers(task.contentType, task.contentId);
            setUsers(list);
            return list;
        } catch (e) {
            return [];
        }
    }, [users, task]);

    useEffect(() => {
        if (task && (perms.canComplete || perms.canManage)) {
            ensureUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task?.id, perms.canComplete, perms.canManage]);

    const openEditorAndStart = async () => {
        if (startsOnOpen) {
            // Opening the editor matters more than the start; a failed start is retried by the next open
            await run(() => TaskTrackingService.startStage(task.id, v())).catch(() => {});
        }
        openEditor();
    };

    const applied = (d) => {
        if (!d) return;
        const results = d.editorResults || [];
        if (results.length === 1) {
            setSnack({ message: `Sent back. ${results[0].message}`, error: results[0].outcome === 'FAILED' });
        } else if (results.length > 1) {
            setSnack({
                message: `Sent back. ${results.map((r) => `${r.languageLabel}: ${r.message}`).join(' ')}`,
                error: results.some((r) => r.outcome === 'FAILED'),
            });
        }
        setDetail(d);
        onChanged && onChanged(d);
        refreshTaskSummary();
        TaskTrackingService.events(d.task.id)
            .then(setEvents)
            .catch(() => {});
    };

    /** Runs a mutation; on a version conflict reloads and tells the user. */
    const run = async (fn) => {
        setBusy(true);
        try {
            const d = await fn();
            applied(d);
            return d;
        } catch (e) {
            if (taskErrorCode(e) === 'STALE_VERSION') {
                setSnack({ message: 'Someone else changed this task, so it was reloaded. Please try again.' });
                await load();
                return null;
            }
            throw e;
        } finally {
            setBusy(false);
        }
    };

    const runOrShow = (fn) => run(fn).catch((e) => setSnack({ message: taskErrorMessage(e), error: true }));
    const v = () => ({ version: task.version });

    // Complete, the language strip and closing the drawer wait for the boxes' last saves
    const postSaved = async () => {
        if (!postSection.current || (await postSection.current.flush())) return true;
        setSnack({ message: "A box couldn't save. Retry it, or copy your text before leaving.", error: true });
        return false;
    };
    const closeDrawer = async () => {
        if (await postSaved()) onClose();
    };
    const openLanguage = async (id) => {
        if ((await postSaved()) && onOpenTask) onOpenTask(id);
    };

    const step = useCompleteStep({
        open,
        taskId,
        detail,
        current,
        link,
        linkedLevel,
        users,
        postSection,
        postSaved,
        applied,
        load,
        onChanged,
        setSnack,
        busy,
        isAssignee,
        focusAction,
        completeBlocked,
        needsDubbingAudio,
        converting,
    });

    const openDialog = async (config) => {
        const needsUsers = (config.fields || []).some((f) => f.type === 'user' || f.type === 'users');
        const list = needsUsers ? await ensureUsers() : users;
        setDialog({
            ...config,
            fields: config.fields.map((f) =>
                f.type === 'user' || f.type === 'users' ? { ...f, users: f.filter ? list.filter(f.filter) : list } : f,
            ),
        });
    };

    const submitDialog = async (values) => {
        await run(() => dialog.submit(values));
        setDialog(null);
    };

    const openFlag = ({ label = 'What do you need?', description, initial } = {}) =>
        openDialog({
            usageName: 'TaskFlagDialog',
            title: 'Flag for PM',
            description,
            submitLabel: 'Flag',
            fields: [{ name: 'note', label, multiline: true, required: true, initial }],
            submit: (vals) => TaskTrackingService.flag(task.id, { ...v(), note: vals.note }),
        });

    // No file behind this task. A cancelled one stays that way, with nothing left to link.
    const noFile = !!task?.waitingForFile;
    const waiting = noFile && task.overallStatus !== 'CANCELLED';
    const waitingVideo = waiting && task.contentType === 'VIDEO';
    const canStartVideo = waitingVideo && !!detail.plan && !detail.plan.linkedContentId;
    const videoFailed = detail?.startedVideo?.status === 'FAILED';
    const videoProcessing = !!detail?.startedVideo && !videoFailed && !detail.startedVideo.ready;
    // What happened while you were away shows when you come back: a file added in the folder's tab, a video processing
    useEffect(() => {
        if (!open) return undefined;
        window.addEventListener('focus', load);
        return () => window.removeEventListener('focus', load);
    }, [open, load]);

    const videoProblem = detail?.post?.subtitleVideo?.removed
        ? 'The video on this post was deleted.'
        : `${task?.languageLabel} isn't on this video yet.`;
    const { menuActions, moveFor, openSendBack, sendBackLink, openPostingDate, canSetPostDate } = usePmActions({
        detail,
        perms,
        current,
        link,
        waiting,
        noFile,
        categories,
        platforms,
        openDialog,
        setEditingPost,
        setSettingDate,
        onChanged,
        onClose,
    });

    // Opened from a board drop: confirm moving to that stage once the task has loaded
    const pendingMove = focusAction?.move;
    useEffect(() => {
        if (!open || !pendingMove || detail?.task?.id !== taskId) return;
        const target = stages.find((s) => s.stageKey === pendingMove);
        const move = target && moveFor(target);
        if (move) move.go();
        else setSnack({ message: "This task can't move to that stage", error: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, pendingMove, detail?.task?.id, taskId]);

    // Opened from a list's Open in editor: start the stage if needed and go to the editor
    const wantsEditor = focusAction === 'editor';
    useEffect(() => {
        if (open && wantsEditor && detail?.task?.id === taskId) openEditorAndStart();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, wantsEditor, detail?.task?.id, taskId]);
    // The translator's room: English and the language side by side
    const translatorRoom = isPost && ['TRANSLATE_TEXT', 'TEXT_QC'].includes(current?.stageKey);

    // One main button for each person and task state (Part 12)
    const word = task && fileWord(task.contentType);
    const startMain =
        waiting && perms.canManage && detail.plan
            ? detail.plan.linkedContentId
                ? { label: `Link ${word}`, onClick: () => setLinking(true) }
                : waitingVideo
                  ? { label: 'Start video translation', onClick: () => setStartingVideo(true) }
                  : { label: `Create ${word}`, onClick: () => setCreatingFile(true) }
            : null;
    const closedStatus = task?.overallStatus === 'COMPLETED' || task?.overallStatus === 'CANCELLED';
    const datePencil = isPost ? canSetPostDate && task.overallStatus !== 'CANCELLED' : perms.canManage && !closedStatus;
    const needsDate =
        isPost && perms.canManage && canSetPostDate && task.overallStatus === 'COMPLETED' && !task.targetDate;
    const main = needsDate ? { label: 'Set posting date', onClick: openPostingDate } : startMain || step.completeButton;

    const showEditor = !!task && !task.redacted && !isPost && !isAudio && !noFile && !videoProcessing && !videoFailed;
    const startLine = !current
        ? null
        : startsOnOpen && showEditor
          ? `Opening it starts ${current.displayName}${
                linkedLevel && !link.editorAtLevel ? ` and moves ${task.languageLabel} to ${linkedLevel} in progress` : ''
            }.`
          : perms.canStart && startBlocked
            ? `${startBlocked} Opening the editor won't start ${current.displayName} until then.`
            : null;
    // One grey hint at most, under the button it explains
    const workHint = step.blockedHint ? null : startLine;
    const mainHint = step.blockedHint || (workHint ? null : step.submitHint);

    const onStepCard = step.stepOpen && (perms.canComplete || perms.canBlock || perms.canFlag);
    const flag = perms.canFlag && {
        label: 'Flag for PM',
        onClick: () => openFlag({ description: 'For example: the planned person is away, or you need a decision.' }),
    };
    const block = perms.canBlock &&
        current?.status !== 'BLOCKED' && { label: 'Block', onClick: () => openDialog(blockDialog(task)) };
    // Someone with no step of their own can still flag; a PM's stays folded until they open it
    const trouble = (onStepCard || !perms.canManage ? [flag, onStepCard && block] : []).filter(Boolean);
    // Outlined, not filled: it's someone else's step (D135)
    const completeFor = current &&
        perms.canComplete &&
        !step.stepOpen && {
            label: current.assignedTo ? `Complete for ${shortName(current.assignedTo)}` : 'Complete this stage',
            onClick: step.openStep,
        };
    const links = sendBackLink ? [{ label: 'Send back', onClick: () => openSendBack() }] : [];

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={closeDrawer}
            ModalProps={{ disableEnforceFocus: true }}
            PaperProps={{
                sx: { width: { xs: '100%', sm: translatorRoom ? 720 : 460 }, maxWidth: '100%', overflow: 'hidden' },
            }}
            className="task-drawer"
        >
            <TaskDrawerHeader
                task={task}
                current={current}
                title={task?.contentTitle || (loading ? 'Loading…' : 'Task')}
                onPencil={datePencil ? openPostingDate : null}
                menuActions={task ? menuActions : []}
                onClose={closeDrawer}
            />
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pb: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {refused && avOnly ? "This isn't available. Posting calendar shows what's ready." : error}
                    </Alert>
                )}
                {loading && !detail && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {task && (
                    <>
                        <TaskWarnings
                            task={task}
                            detail={detail}
                            perms={perms}
                            current={current}
                            busy={busy}
                            onStartAgain={() => setStartingVideo(true)}
                            openDialog={openDialog}
                            runOrShow={runOrShow}
                        />
                        <TaskWork
                            task={task}
                            detail={detail}
                            perms={perms}
                            showEditor={showEditor}
                            subtitleVideo={detail.post?.subtitleVideo}
                            busy={busy}
                            onOpenEditor={openEditorAndStart}
                            onOpenSubtitleVideo={() =>
                                openInEditor({
                                    contentType: 'VIDEO',
                                    contentId: detail.post.subtitleVideo.videoId,
                                    folderPath: detail.post.subtitleVideo.folderPath,
                                    navigate,
                                    setCanNavigateToEdit,
                                    language: detail.post.subtitleVideo.editorLanguage,
                                })
                            }
                            hint={workHint}
                            conversion={conversion}
                            converting={converting}
                            convertLine={convertLine}
                            onConvert={() => setConvertOpen(true)}
                            videoProblem={videoProblem}
                            onFlagVideo={() => openFlag({ initial: videoProblem })}
                            strip={
                                detail.post && (
                                    <PostLanguageStrip
                                        post={detail.post}
                                        task={task}
                                        canManage={perms.canManage}
                                        onOpen={openLanguage}
                                    />
                                )
                            }
                            boxes={
                                detail.post && (
                                    <PostTextSection
                                        key={task.id}
                                        ref={postSection}
                                        detail={detail}
                                        task={task}
                                        current={current}
                                        onReload={load}
                                        isMe={isMe}
                                    />
                                )
                            }
                        />

                        {waiting && (
                            <TaskWaitingCard
                                task={task}
                                plan={detail.plan}
                                canManage={perms.canManage}
                                planned={stages.some((s) => isMe(s.plannedAssignee))}
                                onLink={() => setLinking(true)}
                            />
                        )}
                        {current && onStepCard && (
                            <TaskStepCard
                                task={task}
                                detail={detail}
                                perms={perms}
                                current={current}
                                linkedLevel={linkedLevel}
                                events={events}
                                focusAction={focusAction}
                                step={step}
                                openFlag={openFlag}
                            />
                        )}
                        {needsDate && (
                            <StepBox title="In the Post bank" line="It goes on the calendar once it has a date." />
                        )}

                        {perms.canManage && detail.posting && !noFile && (
                            <PostingSection
                                task={task}
                                posting={detail.posting}
                                platforms={platforms}
                                openDialog={openDialog}
                                onChanged={() => {
                                    load();
                                    onChanged && onChanged();
                                }}
                            />
                        )}

                        <TaskStages
                            task={task}
                            stages={stages}
                            link={link}
                            canManage={perms.canManage}
                            moveFor={moveFor}
                        />
                        <TaskComments
                            events={events}
                            stages={stages}
                            canComment={perms.canComment}
                            busy={busy}
                            onPost={(note) => runOrShow(() => TaskTrackingService.comment(task.id, { ...v(), note }))}
                        />
                        <TaskDetails
                            task={task}
                            detail={detail}
                            categories={categories}
                            platforms={platforms}
                            onShowFolder={(folderPath) => showInFiles({ folderPath, navigate })}
                        />
                        <TaskHistory task={task} events={events} stages={stages} earlierTasks={detail.earlierTasks} />
                    </>
                )}
            </Box>
            {task && <TaskStepActions main={main} secondary={completeFor} hint={mainHint} trouble={trouble} links={links} />}

            {task && (
                <TaskFileDialogs
                    task={task}
                    detail={detail}
                    waiting={waiting}
                    canStartVideo={canStartVideo}
                    videoFailed={videoFailed}
                    linking={linking}
                    setLinking={setLinking}
                    creatingFile={creatingFile}
                    setCreatingFile={setCreatingFile}
                    startingVideo={startingVideo}
                    setStartingVideo={setStartingVideo}
                    onRefreshed={() => {
                        load();
                        onChanged && onChanged();
                        refreshTaskSummary();
                    }}
                    setSnack={setSnack}
                />
            )}
            {isPost && detail.post && (
                <EditPostDialog
                    open={editingPost}
                    postId={detail.post.postId}
                    onClose={() => setEditingPost(false)}
                    onChanged={() => {
                        load();
                        onChanged && onChanged();
                    }}
                />
            )}
            {isPost && detail.post && settingDate && (
                <SetPostingDateDialog
                    post={detail.post}
                    onClose={() => setSettingDate(false)}
                    onSaved={() => {
                        setSettingDate(false);
                        load();
                        onChanged && onChanged();
                    }}
                />
            )}
            <TaskCompleteDialogs task={task} link={link} linkedLevel={linkedLevel} step={step} />

            {task && atAudioConversion && (
                <DubbingTransformDialog
                    open={convertOpen}
                    task={task}
                    onClose={(status, message) => {
                        setConvertOpen(false);
                        if (status === 'success') {
                            setSnack({ message });
                            loadConversion();
                            // Starting it may have started the stage; the list behind shows it too
                            TaskTrackingService.get(task.id)
                                .then(applied)
                                .catch(() => load());
                        }
                    }}
                />
            )}

            {dialog && (
                <TaskActionDialog
                    open={!!dialog}
                    usageName={dialog.usageName}
                    title={dialog.title}
                    description={dialog.description}
                    fields={dialog.fields}
                    submitLabel={dialog.submitLabel}
                    submitColor={dialog.submitColor}
                    onSubmit={submitDialog}
                    onClose={() => setDialog(null)}
                />
            )}

            <TaskSnackbar snack={snack} setSnack={setSnack} />
        </Drawer>
    );
};

export default TaskDrawer;
